import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCpAuth } from "@/contexts/ClientPortalAuthContext";
import { Loader2, LogIn } from "lucide-react";

export default function CpLogin() {
  const { user, login, loading } = useCpAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-[#0a2540]" size={28} />
      </div>
    );
  }
  if (user) return <Navigate to="/client-portal/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome ${r.user?.name || ""}`);
      if (r.user?.must_change_password) {
        navigate("/client-portal/change-password");
      } else {
        navigate("/client-portal/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden md:flex flex-1 bg-[#0a2540] text-white flex-col justify-between p-10">
        <div className="flex items-center gap-3">
          <img src="/branding/griffin-logo.png" alt="Griffin" className="h-10 w-10 rounded-sm bg-white p-0.5" />
          <div>
            <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Griffin Consultancy</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-300">Client Portal</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
            Your books. Your numbers. Anytime.
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            Real-time view of your sales, receivables, payables and statutory compliance —
            uploaded and maintained by your Griffin engagement team.
          </p>
        </div>
        <div className="text-[10px] text-slate-400">© Griffin Consultancy</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5 bg-white p-8 border border-slate-200 rounded-sm shadow-sm" data-testid="cp-login-form">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">Sign in</div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Client Portal</h2>
            <p className="text-xs text-slate-500 mt-1">Use the credentials provided by your Griffin engagement team.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-email">Email</Label>
            <Input
              id="cp-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="cp-login-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-password">Password</Label>
            <Input
              id="cp-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="cp-login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0a2540] hover:bg-[#0a2540]/90"
            data-testid="cp-login-submit"
          >
            {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <LogIn size={16} className="mr-2" />}
            Sign in
          </Button>
          <p className="text-[11px] text-center">
            <Link
              to="/client-portal/forgot-password"
              className="text-[#0a2540] underline-offset-2 hover:underline"
              data-testid="cp-forgot-password-link"
            >
              Forgot your password?
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
